import { ReactElement, useEffect } from "react"

import { FormLabel, TextInput } from "@wfp/ui"

import Select from "react-select"

import { useGetDCFQuery } from "../../../services/marketprices/dataCollectionFrequencies"
import { useGetPriceTypeQuery } from "../../../services/core/priceType"

import { useDataModaUploadContext } from "../../../providers/DataModaUploadContext"
import { useGetCurrenciesByCountryQuery } from "../../../services/core/currencies"

const GlobalFieldMapping = (): ReactElement => {
  const { country, modaFormFields, commonSchema, setCommonSchema, modaForm } = useDataModaUploadContext()

  const { data: frequencies, isFetching: isLoadingFrequencies } = useGetDCFQuery({})
  const { data: priceTypes, isFetching: isLoadingPriceTypes } = useGetPriceTypeQuery()
  const { data: currencies, isFetching: isLoadingCurrencies } = useGetCurrenciesByCountryQuery(country ?? 0, {
    skip: !country,
  })

  const isPriceTypeMapped = modaFormFields.find(field => field.assignedType?.includes("priceType"))
  const isDataSourceMapped = modaFormFields.find(field => field.assignedType?.includes("dataSource"))

  useEffect(() => {
    setCommonSchema((prev: any) => ({
      ...prev,
      frequency: {
        value: modaForm?.dataCollectionFrequencyId || 0,
        type: "int",
      },
    }))
  }, [])

  return (
    <div className="">
      <div className="flex-1">
        <div>
          <FormLabel htmlFor="choice" className="text-2xl">
            Global values
          </FormLabel>
        </div>
        <div className="row">
          <p className="text-sm italic">
            Specify here the values that will be applied to all the rows in the excel file you&apos;re uploading. Leave
            selections blank if you want to map them to specific columns.
          </p>
        </div>
      </div>

      <div className="row-auto mt-4 min-h-[44vh]">
        <div className="grid grid-cols-2 mt-2 gap-16">
          <div>
            <FormLabel id="frequency">Collection Frequency</FormLabel>
            <p className="text-sm text-gray-500 mb-1">How often is the data collected</p>
            <Select
              inputId="frequency"
              placeholder="Select frequency"
              isLoading={isLoadingFrequencies}
              isDisabled={modaForm?.dataCollectionFrequencyId !== undefined}
              options={frequencies ?? []}
              getOptionLabel={option => option.name}
              getOptionValue={option => option.id.toString()}
              value={frequencies?.find(f => f.id === Number(modaForm?.dataCollectionFrequencyId))}
              onChange={e => {
                setCommonSchema((prev: any) => ({
                  ...prev,
                  frequency: {
                    value: e?.id || 0,
                    type: "int",
                  },
                }))
              }}
              isClearable
              menuPosition="fixed"
            />
          </div>
          <div>
            <div>
              <FormLabel id="price-type">Price Type</FormLabel>
              <p className="text-sm text-gray-500 mb-1">Global price type for all the rows here</p>
              <Select
                inputId="price-type"
                isLoading={isLoadingPriceTypes}
                placeholder={isPriceTypeMapped?.fieldName || "Select price type"}
                isDisabled={!!isPriceTypeMapped}
                options={priceTypes ?? []}
                getOptionLabel={option => option.priceTypeName}
                getOptionValue={option => option.priceTypeID.toString()}
                value={priceTypes?.find((p: any) => p.priceTypeID === Number(commonSchema.priceType?.value))}
                onChange={e => {
                  setCommonSchema((prev: any) => ({
                    ...prev,
                    priceType: {
                      value: e?.priceTypeID || 0,
                      type: "int",
                    },
                  }))
                }}
                isClearable
                menuPosition="fixed"
                menuPlacement="auto"
              />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 mt-2 gap-16">
          <div>
            <FormLabel id="currency">Currency</FormLabel>
            <p className="text-sm text-gray-500 mb-1">Currency used in the price values</p>
            <Select
              inputId="currency"
              isLoading={isLoadingCurrencies}
              placeholder="Select currency"
              options={currencies ?? []}
              getOptionLabel={option => option.currencyName}
              getOptionValue={option => option.currencyID.toString()}
              value={currencies?.find((c: any) => c.currencyID === Number(commonSchema.currency?.value))}
              onChange={e => {
                setCommonSchema((prev: any) => ({
                  ...prev,
                  currency: {
                    value: e?.currencyID || 0,
                    type: "int",
                  },
                }))
              }}
              isClearable
              menuPosition="fixed"
              // isDisabled={!!mappingRows.find(item => item.dataType === "currency")}
            />
          </div>
          <div>
            <FormLabel id="form-label">Data Source</FormLabel>
            <p className="text-sm text-gray-500 mb-1">Source of the data</p>
            <TextInput
              id="form-label"
              disabled={!!isDataSourceMapped}
              value={isDataSourceMapped?.fieldName || commonSchema?.dataSource?.value || ""}
              placeholder="type data source"
              onChange={e => {
                setCommonSchema((prev: any) => ({
                  ...prev,
                  dataSource: {
                    value: e.target.value,
                    type: "string",
                  },
                }))
              }}
              className="custom-text-field"
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default GlobalFieldMapping
